import LensIcon from "../icons/lens";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { useNavigate, useLocation, Link } from "react-router-dom";
import React from "react";

import { useAuth } from "../lib/auth";
import { useTranslation } from "../hooks/useTranslation";
import HeaderLoggedInActions from "./HeaderLoggedInActions";

type HeaderProps = {};

const Header: React.FC<HeaderProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();

  const getInitials = () => {
    const name = user?.name;
    const username = user?.username;

    if (name && username) {
      return `${name.charAt(0).toUpperCase()}${username.charAt(0).toUpperCase()}`;
    } else if (name) {
      const words = name.trim().split(" ");
      if (words.length >= 2) {
        return `${words[0].charAt(0).toUpperCase()}${words[1].charAt(0).toUpperCase()}`;
      }
      return name.substring(0, 2).toUpperCase();
    } else if (username) {
      return username.substring(0, 2).toUpperCase();
    }
    return "کا";
  };

  const desktopNav = [
    { to: "/", label: t("navigation.home"), match: (p: string) => p === "/" },
    {
      to: "/transfers",
      label: t("navigation.transfers"),
      match: (p: string) => p.startsWith("/transfers"),
    },
    {
      to: "/news",
      label: t("navigation.news"),
      match: (p: string) => p.startsWith("/news"),
    },
    {
      to: "/livescores",
      label: t("navigation.livescores"),
      match: (p: string) => p.startsWith("/livescores"),
    },
    {
      to: "/feed",
      label: t("navigation.feed"),
      match: (p: string) => p === "/feed" || p.startsWith("/lenz/"),
    },
  ] as const;

  const navClass = (active: boolean) =>
    `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
      active
        ? "text-[#1b4d3e] bg-[#e8f0ec]"
        : "text-[#5c6370] dark:text-gray-300 hover:text-[#1a1c1e] dark:hover:text-white hover:bg-[#f6f5f2] dark:hover:bg-white/5"
    }`;

  const bottomClass = (active: boolean) =>
    `flex flex-col items-center justify-center gap-0.5 p-1.5 min-w-[3.25rem] rounded-md transition-colors ${
      active
        ? "text-[#1b4d3e] dark:text-emerald-400"
        : "text-[#5c6370] dark:text-gray-300"
    }`;

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-[#e6e4df] bg-[#fafaf8]/92 backdrop-blur-md dark:bg-slate-950/90 dark:border-white/10 native-safe-top">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex justify-between py-2.5 md:py-3 items-center gap-3">
            <div className="flex items-center gap-4 min-w-0">
              <Link to="/" className="cursor-pointer flex items-center min-w-0 shrink">
                <img
                  src="/logo.png"
                  alt={t("header.siteName")}
                  className="h-10 w-10 me-2 shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-sm sm:text-md font-bold text-gray-900 dark:text-white leading-tight truncate">
                    {t("header.siteName")}
                  </div>
                  <p className="text-[10px] text-gray-600 dark:text-gray-400 leading-tight truncate">
                    {t("header.siteTagline")}
                  </p>
                </div>
              </Link>

              {/* Desktop content nav */}
              <nav
                className="hidden md:flex items-center gap-1"
                aria-label={t("hub.quickLinks")}
              >
                {desktopNav.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={navClass(item.match(location.pathname))}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="flex items-center gap-0.5 shrink-0">
              <button
                onClick={() => {
                  if (isAuthenticated && user) {
                    navigate("/create");
                  } else {
                    navigate("/login");
                  }
                }}
                className={`p-1.5 me-1 rounded-md transition-colors ${
                  location.pathname === "/create"
                    ? "text-[#1b4d3e] dark:text-emerald-400"
                    : "text-[#5c6370] dark:text-gray-300 hover:text-[#1a1c1e] dark:hover:text-white"
                }`}
                aria-label={t("header.newPost")}
              >
                <svg
                  aria-label={t("header.newPost")}
                  className="w-6 h-6"
                  fill="currentColor"
                  height={24}
                  role="img"
                  viewBox="0 0 24 24"
                  width={24}
                >
                  <title>{t("header.newPost")}</title>
                  <path d="M21 11h-8V3a1 1 0 1 0-2 0v8H3a1 1 0 1 0 0 2h8v8a1 1 0 1 0 2 0v-8h8a1 1 0 1 0 0-2Z" />
                </svg>
              </button>
              {isAuthenticated && user && (
                <HeaderLoggedInActions
                  onNavigate={navigate}
                  messagesPathActive={location.pathname.startsWith("/messages")}
                />
              )}
              {!isAuthenticated && (
                <button
                  onClick={() => navigate("/login")}
                  className="hidden md:inline-flex ms-1 rounded-lg bg-[#1b4d3e] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#163f33] transition-colors"
                >
                  {t("navigation.login")}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Outside header so fixed isn't trapped by header backdrop-blur */}
      <div className="fixed bottom-0 inset-x-0 z-50 border-t border-[#e6e4df] bg-[#fafaf8]/95 backdrop-blur-md dark:bg-slate-950/95 dark:border-white/10 native-safe-bottom md:hidden">
        <nav className="max-w-7xl mx-auto h-14 flex items-center justify-around px-1">
          <button
            onClick={() => navigate("/")}
            className={bottomClass(location.pathname === "/")}
            aria-label={t("navigation.home")}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5Z" />
            </svg>
            <span className="text-[10px] font-medium">{t("navigation.home")}</span>
          </button>

          <button
            onClick={() => navigate("/transfers")}
            className={bottomClass(location.pathname.startsWith("/transfers"))}
            aria-label={t("navigation.transfers")}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
            </svg>
            <span className="text-[10px] font-medium">{t("navigation.transfers")}</span>
          </button>

          <button
            onClick={() => navigate("/livescores")}
            className={bottomClass(location.pathname.startsWith("/livescores"))}
            aria-label={t("navigation.livescores")}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.5h3v7H3v-7Zm7.5-6h3v13h-3v-13Zm7.5 3h3v10h-3v-10Z" />
            </svg>
            <span className="text-[10px] font-medium">{t("navigation.livescores")}</span>
          </button>

          <button
            onClick={() => navigate("/feed")}
            className={bottomClass(
              location.pathname === "/feed" || location.pathname.startsWith("/lenz/")
            )}
            aria-label={t("navigation.feed")}
          >
            <LensIcon size={24} />
            <span className="text-[10px] font-medium">{t("navigation.feed")}</span>
          </button>

          {isAuthenticated && user ? (
            <button
              onClick={() => navigate(`/${user.username || user.id}`)}
              className={bottomClass(false)}
              aria-label={t("navigation.profile")}
            >
              {user.image ? (
                <img
                  src={user.image}
                  alt={user.name || user.username}
                  className="w-7 h-7 rounded-full object-cover"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#1b4d3e] to-[#2d6a56] flex items-center justify-center text-white text-[10px] font-medium">
                  {getInitials()}
                </div>
              )}
              <span className="text-[10px] font-medium">{t("navigation.profile")}</span>
            </button>
          ) : (
            <button
              onClick={() => navigate("/signup")}
              className={bottomClass(location.pathname === "/signup")}
              aria-label={t("navigation.signup")}
            >
              <PersonAddOutlinedIcon sx={{ fontSize: 24 }} />
              <span className="text-[10px] font-medium">{t("navigation.signup")}</span>
            </button>
          )}
        </nav>
      </div>
    </>
  );
};

export default Header;
